"use client";

import { useTranslations } from "next-intl";
import type { Branch } from "@/types";
import type { CityLandingLink, StationLink } from "@/lib/seo/product-breadcrumb";
import { COVERAGE_MIN_BRANCHES, isCampingCategory } from "@/lib/branches/coverage";
import { groupBranchesByRegion } from "@/lib/utils/branch-groups";
import ProductPlaceLinks from "./ProductPlaceLinks";

/**
 * Head of the location section, rendered in the server HTML: the section
 * title (and the coverage summary for many-branch products) plus the
 * "이 상품을 빌릴 수 있는 곳" chips. ProductBranches below is client-only
 * (`ssr: false`), so its own heading is hidden (`hideHeading`) and this one
 * takes its place — same text, same classes.
 */
export default function ProductLocationHeader({
  branches,
  categoryCode,
  cityLandings,
  stations,
  model = null,
}: {
  branches: Branch[];
  categoryCode?: string | null;
  cityLandings: readonly CityLandingLink[];
  stations: readonly StationLink[];
  /** 이 상품이 속한 모델 허브 — 칩 줄 맨 앞 "{모델} 전체 보기" */
  model?: { name: string; path: string } | null;
}) {
  const tCoverage = useTranslations("product.coverage");
  const tBranches = useTranslations("product.branches");
  const tMap = useTranslations("product.locationMap");
  const tLinks = useTranslations("product.contextLinks");

  const physical = branches.filter((b) => b.branch_type !== "delivery");
  if (physical.length === 0) return null;

  const isCoverage = physical.length >= COVERAGE_MIN_BRANCHES;
  const isCamping = isCampingCategory(categoryCode);

  return (
    <div className="mb-4 space-y-4">
      {isCoverage ? (
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
            {tCoverage(isCamping ? "titleCamping" : "title")}
          </h2>
          <p className="text-sm text-gray-500">
            {tCoverage(isCamping ? "summaryCamping" : "summary", {
              regions: groupBranchesByRegion(physical, tMap("otherRegion")).length,
              count: physical.length,
            })}
          </p>
        </div>
      ) : (
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
          {tBranches("location")}
        </h2>
      )}
      <ProductPlaceLinks
        title={tLinks("whereTitle")}
        cityLandings={cityLandings}
        stations={stations}
        countLabel={(count) => tLinks("branchCount", { count })}
        model={model ? { label: tLinks("modelAll", { name: model.name }), path: model.path } : null}
      />
    </div>
  );
}
